import { ROUTE_DATA_SOURCES, ROUTE_PIPELINES } from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { FLOW_TYPE_ELT, FLOW_TYPE_CUSTOM } from 'js/constants/pipelineConstants';
import flowTypes from 'new/components/flows/types/flowTypes';
import { createApiServerFlow } from 'js/actions';
import { assignFlowToProjectIfPresent } from 'new/utils/projectAssignment';

const NEW_FLOW_URI = `${ROUTE_CONNECT}${ROUTE_DATA_SOURCES}/new`;

interface NavigateToFlowOptions {
  history: any;
  dispatch: any;
  selectedFlowType: string;
  eltFlowEnabled?: boolean;
  customFlowEnabled?: boolean;
  currentProjectId?: number | null;
  returnPathOnDismiss?: string;
}

/**
 * Centralized navigation logic for creating different flow types
 * Handles routing and feature flag checks for all flow types
 */
export const navigateToFlowCreation = async ({
  history,
  dispatch,
  selectedFlowType,
  currentProjectId,
  returnPathOnDismiss,
}: NavigateToFlowOptions): Promise<void> => {
  // Streaming/Flex Flow - standard flow creation
  if (selectedFlowType === flowTypes.streaming) {
    history.push({
      pathname: NEW_FLOW_URI,
      state: returnPathOnDismiss ? { returnPath: returnPathOnDismiss } : undefined,
    });
    return;
  }

  // RAG Flow - navigate to RAG flow creation
  if (selectedFlowType === flowTypes.genai_rag) {
    history.push({
      pathname: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/genai_rag/new`,
      state: returnPathOnDismiss ? { returnPath: returnPathOnDismiss } : undefined,
    });
    return;
  }

  // API Server Flow - create flow first, then navigate to edit page
  if (selectedFlowType === flowTypes.api_server) {
    try {
      const response = await dispatch(createApiServerFlow());
      const flowId = response.result?.flows?.id?.[0];

      if (flowId) {
        try {
          await assignFlowToProjectIfPresent({
            dispatch,
            flowNodeId: flowId,
            currentProjectId,
          });
        } catch (error) {
          console.error('Failed to assign flow to project:', error);
        }

        history.push(`${ROUTE_CONNECT}${ROUTE_PIPELINES}/api_server/${flowId}/edit`);
      }
    } catch (error) {
      console.error('Failed to create API Server flow:', error);
    }
    return;
  }

  // BYO (custom) flow — single create URL, no resourceType segment.
  if (selectedFlowType === FLOW_TYPE_CUSTOM || selectedFlowType === 'byo') {
    history.push({
      pathname: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/new`,
      state: returnPathOnDismiss ? { returnPath: returnPathOnDismiss } : undefined,
    });
    return;
  }

  // Default case - navigate to flow type specific source creation
  history.push({
    pathname: `${ROUTE_CONNECT}${ROUTE_PIPELINES}/${selectedFlowType}/source/new`,
    state: returnPathOnDismiss ? { returnPath: returnPathOnDismiss } : undefined,
  });
};
